import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsPageView } from '@chilecompra/react-kit/analytics'
import { useEffectOnce } from '@chilecompra/react-kit'
import { Button, Input, Spinner, Toast, useInput, useViewport } from '@chilecompra/react-kit/components'
import { PHONE_NUMBERS_STARTS_WITH } from '../../../config/settings/constants'
import { checkInputErrorStartsWith, formatOnlyLetters } from '../../../modules/utils/checkers'
import {
  checkInputErrorMaxLength,
  checkInputErrorMinLength,
  checkInputErrorOnlyNumbers,
  checkInputErrorRequired,
  formatOnlyNumbers
} from '@chilecompra/react-kit/utils'
import {
  onRegisterBy,
  onResetRegisterWizardState,
  onSaveTemporaryPersonalInformation,
  registerFlowThunk
} from '../RegisterWizard.actions'
import {
  DesktopNavigation,
  Hidden,
  Image,
  Instructions,
  MobileButtons,
  MobileNavigation,
  PhoneNumberPrefix,
  Root,
  Section,
  Strong,
  Title,
  ToastContainer
} from './RegisterWizardPersonalInformation.styles'

/**
 * The RegisterWizardPersonalInformation's container.
 */
const RegisterWizardPersonalInformation = props => {
  const { onChangeStep } = props
  const { guest, registerType } = useSelector(state => state.registerWizard)
  const { error, loading } = useSelector(state => state.uniquePassAuth)
  const { name, lastName, position } = useSelector(state => state.registerWizard.guest)
  const { size } = useViewport()
  const dispatch = useDispatch()
  const flow = 'INICIO_PASO_1_BIS'

  const handleRegisterByUniquePass = () => {
    dispatch(onRegisterBy('email-verify'))
    onChangeStep('email-verify')
  }

  const {
    error: nameError,
    value: nameValue,
    onChange: handleNameChange
  } = useInput({
    initialValue: name,
    errorCallbacks: [checkInputErrorRequired()],
    overwriteCallback: () => name
  })
  const {
    error: lastNameError,
    value: lastNameValue,
    onChange: handleLastNameChange
  } = useInput({
    initialValue: lastName,
    errorCallbacks: [checkInputErrorRequired()],
    overwriteCallback: () => lastName
  })
  const {
    error: phoneError,
    value: phoneValue,
    onChange: handlePhoneChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(),
      checkInputErrorStartsWith('El número debe comenzar con 9 o 2.', PHONE_NUMBERS_STARTS_WITH),
      checkInputErrorOnlyNumbers(),
      checkInputErrorMinLength(9, 'Este campo permite 8 dígitos.'),
      checkInputErrorMaxLength(9, 'Este campo permite 8 dígitos.')
    ],
    formatCallbacks: [formatOnlyNumbers]
  })
  const {
    error: positionError,
    value: positionValue,
    onChange: handlePositionChange
  } = useInput({
    initialValue: position,
    errorCallbacks: [checkInputErrorRequired()],
    formatCallbacks: [formatOnlyLetters],
    overwriteCallback: () => position
  })

  const handleBack = () => {
    dispatch(onResetRegisterWizardState())
    onChangeStep('welcome')
  }

  const handleNext = event => {
    event.preventDefault()
    dispatch(
      onSaveTemporaryPersonalInformation({
        lastName: lastNameValue,
        gender: guest.gender,
        name: nameValue,
        phone: phoneValue,
        phoneCode: '56',
        position: positionValue
      })
    )
    handleRegisterByUniquePass()
  }

  const canNotSubmit = useCallback(() => {
    const hadErrors = nameError !== '' || lastNameError !== '' || phoneError !== '' || positionError !== ''

    const hadInvalidValues = nameValue === '' || lastNameValue === '' || phoneValue === '' || positionValue === ''

    return hadErrors || hadInvalidValues || error
  }, [lastNameError, nameError, phoneError, positionError, nameValue, lastNameValue, phoneValue, positionValue, error])

  const disabledSubmit = canNotSubmit()

  useAnalyticsPageView('/paso/1/informacion_personal')

  useEffectOnce(() => {
    dispatch(registerFlowThunk({ flow }))
  }, [dispatch])

  const renderToastSection = () => {
    return (
      <ToastContainer>
        <Toast
          tabIndex="0"
          margin={size.isTiny ? '' : size.isDownToLarge ? '-10px 0 0 0' : '32px 0 0'}
          padding="8px 16px"
          severity="info"
        >
          Mercado Público utilizará esta información para crear tu perfil de <Strong>administrador</Strong> en la
          plataforma.
        </Toast>
      </ToastContainer>
    )
  }

  return (
    <Root>
      {!loading && registerType === 'by-unique-pass'}
      <Section container>
        <Section item xs={12} md={6}>
          <Title tabIndex="0" aria-label="Paso 1: Completa con tu información">
            Completa con tu información personal
          </Title>
          {loading && (
            <Section
              role="status"
              aria-label="Cargando..."
              container
              display="flex"
              justifyContent="center"
              alignItems="center"
              margin={size.isTiny ? '150px 0' : '90px 0'}
            >
              <Spinner />
            </Section>
          )}

          {!error && !loading && (
            <Section>
              <Instructions tabIndex="0">Ingresa tu número de teléfono laboral.</Instructions>
              <Section
                container
                direction="column"
                margin="15px 0"
                maxWidth={size.isDownToMedium || size.isUpToSmall ? '384px' : '100%'}
              >
                <Section item>
                  <Input
                    autoFocus
                    disabled={name !== ''}
                    error={nameError}
                    label="Nombres"
                    aria-label="Nombres"
                    loading={loading}
                    onChange={handleNameChange}
                    value={nameValue}
                  />
                </Section>
                <Section item>
                  <Input
                    disabled={lastName !== ''}
                    error={lastNameError}
                    label="Apellidos"
                    aria-label="Apellidos"
                    loading={loading}
                    onChange={handleLastNameChange}
                    value={lastNameValue}
                  />
                </Section>
                <Section item>
                  <Input
                    prefix={<PhoneNumberPrefix>+56</PhoneNumberPrefix>}
                    error={phoneError}
                    label="Número de teléfono laboral o móvil"
                    aria-label="Número de teléfono laboral o móvil"
                    onChange={handlePhoneChange}
                    placeholder="981234567"
                    value={phoneValue}
                    autoFocus
                  />
                </Section>
                <Section item>
                  <Input
                    error={positionError}
                    label="Cargo"
                    aria-label="Cargo"
                    onChange={handlePositionChange}
                    placeholder="Ej. Encargado de compras"
                    value={positionValue}
                  />
                </Section>
              </Section>
              {(size.isTiny || size.isDownToLarge) && renderToastSection()}
            </Section>
          )}
          {error && error === 'SOMETHING_WENT_WRONG_ERROR' && (
            <Toast
              tabIndex="0"
              margin={size.isTiny ? '22px 0 13px 0' : '34px 0 0'}
              padding="8px 16px"
              severity="warning"
            >
              Error en servicio externo, puedes intentar nuevamente en unos minutos para continuar con tu inscripción.
            </Toast>
          )}

          {error && error === 'UNDERAGE_USER_ERROR' && (
            <Toast
              tabIndex="0"
              severity="warning"
              margin={size.isTiny ? '22px 0 13px 0' : '34px 0 0'}
              padding="16px 24px"
            >
              El Rut ingresado corresponde a una persona menor de edad según la información del Registro Civil.
            </Toast>
          )}

          {error && error === 'DECEASED_PERSON_ERROR' && (
            <Toast
              tabIndex="0"
              severity="warning"
              margin={size.isTiny ? '22px 0 13px 0' : '34px 0 0'}
              padding="16px 24px"
            >
              El Rut ingresado corresponde a una persona fallecida según la información del Registro Civil.
            </Toast>
          )}
        </Section>
        <Section
          item
          xs={12}
          md={6}
          margin="-78px 0 0"
          padding={size.isTiny ? '' : size.isDownToLarge ? '' : '0 0 0 51px'}
        >
          <Hidden lgDown>
            <Image src="/assets/img/steps/paso2.svg" alt="Ilustración Registro vía ClaveÚnica" />
          </Hidden>
          {!size.isTiny && !size.isDownToLarge && renderToastSection()}
        </Section>
      </Section>

      {!error && !loading && !size.isTiny && (
        <Section container maxWidth="568px">
          <DesktopNavigation>
            <Button
              tabIndex="0"
              aria-label="Cancelar"
              onClick={handleBack}
              type="button"
              width="156px"
              variant="outlined"
            >
              Cancelar
            </Button>
            <Button
              tabIndex="0"
              aria-label="Siguiente"
              color="primary"
              disabled={disabledSubmit}
              type="button"
              width="156px"
              onClick={handleNext}
            >
              Siguiente
            </Button>
          </DesktopNavigation>
        </Section>
      )}

      {error && !loading && !size.isTiny && (
        <Section container maxWidth="568px">
          <DesktopNavigation>
            <Button
              tabIndex="0"
              aria-label="Cancelar"
              onClick={handleBack}
              type="button"
              width={size.isDownToLarge ? '100%' : '156px'}
              variant="outlined"
            >
              Cancelar
            </Button>
          </DesktopNavigation>
        </Section>
      )}

      {!error && !loading && size.isTiny && (
        <Section container>
          <MobileNavigation>
            <MobileButtons
              tabIndex="0"
              aria-label="Siguiente"
              color="primary"
              disabled={disabledSubmit}
              type="button"
              onClick={handleNext}
            >
              Siguiente
            </MobileButtons>
            <MobileButtons tabIndex="0" aria-label="Cancelar" onClick={handleBack} type="button" variant="outlined">
              Cancelar
            </MobileButtons>
          </MobileNavigation>
        </Section>
      )}

      {error && !loading && size.isTiny && (
        <Section>
          {renderToastSection()}
          <MobileNavigation>
            <MobileButtons tabIndex="0" aria-label="Cancelar" onClick={handleBack} type="button" variant="outlined">
              Cancelar
            </MobileButtons>
          </MobileNavigation>
        </Section>
      )}
    </Root>
  )
}

RegisterWizardPersonalInformation.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardPersonalInformation.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardPersonalInformation
